<template>
  <table class="table is-fullwidth is-narrow is-striped is-hoverable">
    <thead>
    <tr class="meta-data" >
      <td colspan="6" class="has-text-right">
        Last updated: {{ lastUpdated }}
        <font-awesome-icon icon="spinner" spin v-show="isLoading"  />
      </td>
    </tr>
    <tr>
      <th v-if="isSelectable"></th>
      <th>ID</th>
      <th>Origin</th>
      <th>Destination</th>
      <th>Status</th>
      <th>
        Milestone Progress
        <b-tooltip label="Green = executed, Red = missed, Gray = upcoming" multilined position="is-top">
          <font-awesome-icon  icon="info-circle"/>
        </b-tooltip>
      </th>
    </tr>
    </thead>

    <tbody>
    <tr v-if="companyJobs.length === 0 && !isLoading">
      <td colspan="6" class="has-text-centered">
        There are currently no transactions that match the selected state.
        <br/>
        This table will automatically refresh every {{ refreshTimerInSeconds }} seconds.
      </td>
    </tr>
    <tr v-else-if="companyJobs.length === 0 && isLoading">
      <td colspan="6" class="has-text-centered">
        <font-awesome-icon icon="spinner" spin v-show="isLoading"  />  Checking for new transactions.
      </td>
    </tr>

    <tr is="TableRow"
        :is-selectable="isSelectable"
        v-on:selectionChange-row="handleSelectedRow"
        :selected="selectedCompanyJobKeys.includes(job.companyJobKey)"
        v-for="job in companyJobs"
        :key="job.companyJobKey"
        :is-job-certified="job.companyJobCarrierCertifiedAccurateFlag"
        :company-job="job">
    </tr>
    </tbody>
  </table>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { format } from 'date-fns'

import TableRow from './row'

export default {
  name: 'TransactionsTable',

  props: {
    companyJobs: {
      type: Array,
      required: true,
      default: () => []
    },
    isSelectable: {
      type: Boolean,
      default: false
    },
    autoUpdate: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      isLoading: false,
      lastUpdated: null,
      selectedCompanyJobKeys: [],
      refreshTimerInSeconds: 30
    }
  },

  components: {
    TableRow
  },

  created() {
    this.fetchExecutionLog()
    const fetch_timer = this.refreshTimerInSeconds * 1000

    if (this.autoUpdate) {
      this.refreshTimerId = window.setInterval(async () => {
        await this.fetchExecutionLog()
      }, fetch_timer)
    }
  },

  beforeDestroy() {
    window.clearInterval(this.refreshTimerId)
  },

  computed: {
    ...mapGetters('user', ['isFactoring', 'isShipper', 'isCarrier', 'accountInfo'])
  },

  methods: {
    ...mapActions('companyJobs', {
      _fetchExecutionLog: 'fetchExecutionLogForInProgress',
      completeShipperReview: 'completeShipperReview'
    }),

    async fetchExecutionLog() {
      try {
        this.isLoading = true
        if (this.isFactoring) {
          const factoringProviderKey = this.accountInfo.factoringProvider.factoringProviderKey
          await this._fetchExecutionLog({ factoringProviderKey })
        } else if (this.isCarrier) {
          await this._fetchExecutionLog({ accountKey: this.accountInfo.accountKey })
        } else {
          await this._fetchExecutionLog()
        }
      } catch (err) {
        console.log('Unable to fetch execution log: ', err)
      } finally {
        this.isLoading = false
        this.lastUpdated = format(Date.now(), 'h:mm A')
      }
    },

    handleSelectedRow({ isSelected, companyJobKey }) {
      // convert the selection Array in to a Set to make removing items easy
      const selected = new Set(this.selectedCompanyJobKeys)
      if (isSelected) {
        selected.add(companyJobKey)
      } else {
        selected.delete(companyJobKey)
      }

      this.selectedCompanyJobKeys = Array.from(selected)
      this.$emit('selectionChange', this.selectedCompanyJobKeys)
    }
  }
}
</script>

<style scoped lang="scss">
.meta-data {
  td {
    border-width: 0;
    font-size: 0.8rem;
  }
}
</style>
